import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  useProjectResourcesRetrieval,
  useRetrieveDiscussions,
} from "@planzone/view-models";
import { Props } from "./types";
import { Discussions } from "./discussions";
import { CreationMenu } from "./creationMenu";
import { Loader } from "./loader";
import styles from "./styles.scss";

const DISCUSSIONS_PAGE_SIZE = 5;

function DiscussionsPage({ project }: Props) {
  const { t } = useTranslation();
  const {
    discussions,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    currentPage,
    canLoadMore,
    retrieveDiscussions,
  } = useRetrieveDiscussions(project);
  // The mentions of the comments without recipients are looked for among the project members.
  const {
    projectResources,
    isRequestPending: isResourceRequestPending,
    isRequestSuccess: isResourceRequestSuccess,
    isRequestFailure: isResourceRequestFailure,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval(project);
  const resources = projectResources.map(({ resource }) => resource);

  React.useEffect(() => {
    !!project.uuid &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveDiscussions({ project, page: 0, size: DISCUSSIONS_PAGE_SIZE });
  }, [project.uuid]);

  const loadNextPage = () => {
    !isRequestPending &&
      canLoadMore &&
      retrieveDiscussions({
        project,
        page: currentPage,
        size: DISCUSSIONS_PAGE_SIZE,
      });
  };

  React.useEffect(() => {
    !!project.uuid &&
      !isResourceRequestPending &&
      !isResourceRequestFailure &&
      !isResourceRequestSuccess &&
      retrieveProjectResources(project);
  }, [project.uuid]);

  return (
    <div className={styles.discussionsPage}>
      <Helmet>
        <title>Planzone • {t("projectPage.discussionsPage.title")}</title>
        <meta name="description" content={project.description} />
      </Helmet>
      <div className={styles.header}>
        <CreationMenu project={project} />
      </div>
      <div className={styles.main}>
        {isRequestPending && currentPage === 0 ? (
          <Loader />
        ) : (
          <Discussions
            project={project}
            discussions={discussions}
            resources={resources}
            isRequestPending={isRequestPending}
            canLoadMore={canLoadMore}
            loadNextPage={loadNextPage}
          />
        )}
      </div>
    </div>
  );
}

export { DiscussionsPage };
